import { Component, OnInit, HostListener } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Router, NavigationExtras, NavigationEnd } from '@angular/router';
import { ControllerForViewBase } from '../framework/controller-for-view-base';
import { PageContainerService, ClientAssetDataService, TimerService } from '../../services';
import {
  ProjectDto,
  GetProjectsByUserCommandRequest,
  GetProjectsByUserCommandResponse,
  ClientDto, AssetDto,
  UserRoleDto
} from "../../data/CommandServiceDtos.generated";
import { faSearch, faChevronLeft, faChevronRight, faStar, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ProjectCardMetricsDto } from '../project-thumbnail/project-card-metrics.model';


@Component({
  standalone: false,
  selector: 'landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss']
})
export class LandingPageComponent extends ControllerForViewBase implements OnInit {
  clientList: ClientDto[] = [];
  assetList: AssetDto[] = [];
  cachedAssets: AssetDto[] = [];
  projects: ProjectDto[] = [];
  cachedProjects: ProjectDto[] = [];
  totalCount = 0;
  isLoadingProjects = true;
  clientCount: number = 0;
  assetControl = new FormControl();
  clientControl = new FormControl();
  projectSortControl = new FormControl();
  projectSearchControl = new FormControl('');
  activeProjectsSelected: string = "active";
  projectTypes: boolean[] = [true, false, null];
  userRoles: UserRoleDto[];
  viewMode: 'projects' | 'features' = 'projects';
  isFeatureDetail: boolean = false;
  favoriteProjects: ProjectDto[] = [];
  recentProjects: ProjectDto[] = [];
  cardMetricsById: { [projectId: number]: ProjectCardMetricsDto } = {};
  metricsPendingById: { [projectId: number]: boolean } = {};
  isLoadingCardMetrics = false;
  private lastInitializedUrl: string = null;
  private projectsLoadInFlight = false;
  private metricsLoadInFlight = false;
  private searchDebounceHandle: any;
  private projectsLoadToken = 0;
  private metricsLoadToken = 0;
  private projectIndexCache: ProjectDto[] = [];
  private projectCacheQueryKey = '';
  private projectCacheReady = false;
  clientMenuOpen = false;
  assetMenuOpen = false;

  faSearch = faSearch;
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faStar = faStar;
  faSpinner = faSpinner;
  readonly projectSkeletonCards = [1, 2, 3, 4, 5, 6, 7, 8];

  readonly pageSizeOptions: number[] = [10, 20, 50, 100];
  pageSizeControl = new FormControl(12);
  pageSize: number = 12;
  currentPage: number = 1;
  private readonly pageSizeStorageKey = 'projectsPageSize';
  // One request covers a normal page: the command fans the per-project work out server side,
  // so splitting the ids across many small requests only adds round trips.
  private readonly cardMetricsBatchSize = 25;

  constructor(public pcs: PageContainerService, public clientOptionsState: ClientAssetDataService, private router: Router, public timerService: TimerService) {
    super(pcs);
  }

  ngOnInit() {
    this.timerService.resetTimer();
    this.restorePageSize();
    // Default filter state on page load: All clients + All assets + All projects
    this.clientControl.setValue(0);
    this.assetControl.setValue(0);
    this.activeProjectsSelected = 'all';
    this.projectSortControl.setValue('latest-desc');
    this.initializeFeatureViewFromRoute(this.router.url);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.initializeFeatureViewFromRoute(event.urlAfterRedirects);
      }
    });

    // Listen to sidebar filter changes
    window.addEventListener('sidebarFilterChange', (event: any) => {
      this.handleSidebarFilterChange(event.detail);
    });

    // Listen to sidebar toggle to adjust layout
    window.addEventListener('sidebarToggle', () => {
      // Trigger change detection to adjust layout
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 300); // Wait for sidebar transition
    });

    // Listen for favorites/recents updates from project thumbnails / project views
    window.addEventListener('favoritesUpdated', () => {
      this.loadFavorites();
    });
    window.addEventListener('recentProjectsUpdated', () => {
      this.loadRecentProjects();
    });

    // Apply defaults immediately (before data loads)
    this.filterClients(0);
  }

  loadRecentProjects(): void {
    this.recentProjects = JSON.parse(localStorage.getItem('recentProjects') || '[]');
  }
  loadFavorites() {
    const favoriteIds = this.readFavoriteIds();
    if (!favoriteIds.length) {
      this.favoriteProjects = [];
      return;
    }

    const byId = new Map<number, ProjectDto>();
    const addProject = (project: ProjectDto) => {
      const id = Number(project && project.ProjectId);
      if (!isNaN(id)) {
        byId.set(id, project);
      }
    };
    (this.favoriteProjects || []).forEach(addProject);
    (this.projects || []).forEach(addProject);
    (this.projectIndexCache || []).forEach(addProject);

    this.favoriteProjects = favoriteIds
      .map(id => byId.get(id))
      .filter((project): project is ProjectDto => !!project);
  }

  private readFavoriteIds(): number[] {
    try {
      const raw = JSON.parse(localStorage.getItem('favoriteProjects') || '[]');
      if (!Array.isArray(raw)) {
        return [];
      }
      return raw
        .map((id: any) => Number(id))
        .filter((id: number) => !isNaN(id) && id > 0);
    } catch {
      return [];
    }
  }

  onToggleFavorite(project: ProjectDto) {
    if (this.favoriteProjects.some(p => p.ProjectId === project.ProjectId)) {
      this.favoriteProjects = this.favoriteProjects.filter(p => p.ProjectId !== project.ProjectId);
    } else {
      this.favoriteProjects.push(project);
    }
    this.loadFavorites(); // sync with localStorage state from thumbnail
  }

  getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
    const years = Math.floor(days / 365);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }

  handleSidebarFilterChange(detail: any) {
    if (detail.client !== undefined) {
      this.clientControl.setValue(detail.client);
      if (detail.client.ClientId) {
        this.clientSelected();
      }
    }
    if (detail.asset !== undefined) {
      this.assetControl.setValue(detail.asset);
      if (detail.asset.AssetId) {
        this.assetSelected();
      }
    }
    if (detail.projectType) {
      this.activeProjectsSelected = detail.projectType;
      this.onProjectTypeChange(detail.projectType);
    }
    if (detail.sort) {
      this.projectSortControl.setValue(detail.sort);
      this.projectSortChanged();
    }
  }



  ngAfterViewInit() {
    console.log("After View Init called");
    //this.loadState();
  }

  onlyTwoItems() {
    if (this.projects.length === 2)
      return true;

    return false;

  }

  get isAdmin(): Boolean {
    return this.pcs.clientSessionService.isAdmin;
  }

  get hasRecentProject(): boolean {
    return !!(this.recentProjects && this.recentProjects.length > 0);
  }

  get canOpenProjectSetup(): boolean {
    return this.hasRecentProject || !!this.isAdmin;
  }

  goToProjectSummary() {
    const projectId = this.recentProjects?.[0]?.ProjectId;
    if (projectId) {
      this.router.navigate(['/project', projectId]);
    }
  }

  goToProjectSetup() {
    const projectId = this.recentProjects?.[0]?.ProjectId;
    if (projectId) {
      this.router.navigate(['/edit-project', projectId]);
      return;
    }
    if (this.isAdmin) {
      this.router.navigate(['new-project']);
    }
  }

  get totalPages(): number {
    if (!this.totalCount) {
      return 1;
    }
    if (this.pageSize <= 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get pagedProjects(): ProjectDto[] {
    return this.projects || [];
  }

  get showingFrom(): number {
    if (!this.totalCount) {
      return 0;
    }
    if (this.pageSize <= 0) {
      return 1;
    }
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingTo(): number {
    if (!this.totalCount) {
      return 0;
    }
    if (this.pageSize <= 0) {
      return this.totalCount;
    }
    return Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  get showPagination(): boolean {
    return this.totalCount > 0;
  }

  get visiblePages(): Array<number | string> {
    const total = this.totalPages;
    const current = this.currentPage;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: Array<number | string> = [1];
    if (current > 3) {
      pages.push('...');
    }

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < total - 2) {
      pages.push('...');
    }
    pages.push(total);
    return pages;
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.scrollProjectListToTop();
    this.loadProjects(true);
  }

  onPageSizeChange() {
    const value = Number(this.pageSizeControl.value);
    this.pageSize = isNaN(value) ? 12 : value;
    localStorage.setItem(this.pageSizeStorageKey, String(this.pageSize));
    this.reloadPortfolio(true, true, false);
  }

  private restorePageSize() {
    const saved = localStorage.getItem(this.pageSizeStorageKey);
    if (saved !== null) {
      const parsed = Number(saved);
      if (!isNaN(parsed) && (parsed === 0 || this.pageSizeOptions.includes(parsed))) {
        this.pageSize = parsed;
        this.pageSizeControl.setValue(parsed);
        return;
      }
    }
    this.pageSize = 12;
    this.pageSizeControl.setValue(12);
  }

  private resetPagination() {
    this.currentPage = 1;
  }

  private scrollProjectListToTop() {
    const container = document.querySelector('.landing-right-col') as HTMLElement;
    if (container) {
      container.scrollTop = 0;
      return;
    }
    const list = document.getElementById('project-list');
    if (list) {
      list.scrollTop = 0;
    }
  }

  async loadData() {
    this.currentPage = 1;
    this.isLoadingProjects = true;
    await this.loadProjects();
  }

  private reloadPortfolio(resetPage = false, showBusy = false, invalidateCache = true) {
    if (invalidateCache) {
      this.clearProjectListCache();
    }
    if (resetPage) {
      this.currentPage = 1;
    }
    this.scrollProjectListToTop();
    this.loadProjects(showBusy);
  }

  private selectedClientId(): number {
    const value = this.clientControl.value;
    return (value && typeof value === 'object' && value.ClientId) ? value.ClientId : 0;
  }

  private selectedAssetId(): number {
    const value = this.assetControl.value;
    return (value && typeof value === 'object' && value.AssetId) ? value.AssetId : 0;
  }

  private buildProjectsRequest(skip?: number, take?: number): GetProjectsByUserCommandRequest {
    const resolvedSkip = skip != null
      ? skip
      : Math.max(0, (this.currentPage - 1) * (this.pageSize > 0 ? this.pageSize : 0));
    const resolvedTake = take != null ? take : this.pageSize;
    const page = resolvedTake > 0 ? Math.floor(resolvedSkip / resolvedTake) + 1 : this.currentPage;
    return {
      Page: page < 1 ? 1 : page,
      PageSize: resolvedTake,
      Skip: resolvedSkip,
      ClientId: this.selectedClientId(),
      AssetId: this.selectedAssetId(),
      ActiveFilter: this.activeProjectsSelected || 'all',
      Search: (this.projectSearchControl.value || '').trim(),
      Sort: this.projectSortControl.value || 'latest-desc',
      FavoriteProjectIds: this.readFavoriteIds()
    };
  }

  private getProjectQueryKey(): string {
    return [
      this.selectedClientId(),
      this.selectedAssetId(),
      this.activeProjectsSelected || 'all',
      (this.projectSearchControl.value || '').trim().toLowerCase(),
      this.projectSortControl.value || 'latest-desc'
    ].join('|');
  }

  private clearProjectListCache() {
    this.projectIndexCache = [];
    this.projectCacheReady = false;
    this.projectCacheQueryKey = '';
  }

  private ensureProjectCacheForCurrentQuery() {
    const key = this.getProjectQueryKey();
    if (key !== this.projectCacheQueryKey) {
      this.projectIndexCache = [];
      this.projectCacheReady = false;
      this.projectCacheQueryKey = key;
    }
  }

  private getVisibleRange(): { start: number; end: number } {
    if (this.pageSize <= 0) {
      return { start: 0, end: this.projectCacheReady ? this.getEffectiveTotalCount() : 0 };
    }
    const start = Math.max(0, (this.currentPage - 1) * this.pageSize);
    return { start, end: start + this.pageSize };
  }

  private getEffectiveTotalCount(): number {
    if (this.totalCount > 0) {
      return this.totalCount;
    }
    for (let i = this.projectIndexCache.length - 1; i >= 0; i--) {
      if (this.projectIndexCache[i]) {
        return i + 1;
      }
    }
    return 0;
  }

  private resolveProjectTotalCount(reportedTotal: number | null | undefined, projects: ProjectDto[], skip: number): number {
    if (reportedTotal != null && reportedTotal > 0) {
      return reportedTotal;
    }
    if (projects.length > 0) {
      return skip + projects.length;
    }
    return 0;
  }

  private unwrapProjectsResponse(response: GetProjectsByUserCommandResponse | any): GetProjectsByUserCommandResponse {
    if (response && response.Payload && (response.Payload.Projects || response.Payload.TotalCount != null)) {
      return response.Payload;
    }
    return response;
  }

  private cacheCovers(start: number, end: number): boolean {
    if (!this.projectCacheReady) {
      return false;
    }
    const effectiveTotal = this.getEffectiveTotalCount();
    if (effectiveTotal === 0) {
      return true;
    }
    const limit = Math.min(end, effectiveTotal);
    if (limit <= start) {
      return true;
    }
    for (let i = start; i < limit; i++) {
      if (!this.projectIndexCache[i]) {
        return false;
      }
    }
    return true;
  }

  private getMissingRange(start: number, end: number): { start: number; end: number } {
    if (!this.projectCacheReady) {
      return this.pageSize <= 0 ? { start: 0, end: 0 } : { start, end };
    }
    const effectiveTotal = this.getEffectiveTotalCount();
    if (effectiveTotal === 0) {
      return { start, end: start };
    }
    const limit = Math.min(end, effectiveTotal);
    let missStart = -1;
    let missEnd = start;
    for (let i = start; i < limit; i++) {
      if (!this.projectIndexCache[i]) {
        if (missStart < 0) {
          missStart = i;
        }
        missEnd = i + 1;
      }
    }
    if (missStart < 0) {
      return { start, end: start };
    }
    return { start: missStart, end: missEnd };
  }

  private mergeProjectsIntoCache(projects: ProjectDto[], startIndex: number) {
    if (!projects || !projects.length) {
      return;
    }
    for (let i = 0; i < projects.length; i++) {
      this.projectIndexCache[startIndex + i] = projects[i];
    }
  }

  private applyVisibleProjectsFromCache() {
    const range = this.getVisibleRange();
    const effectiveTotal = this.getEffectiveTotalCount();
    const limit = this.projectCacheReady
      ? Math.min(range.end, effectiveTotal)
      : range.end;
    const slice: ProjectDto[] = [];
    for (let i = range.start; i < limit; i++) {
      if (this.projectIndexCache[i]) {
        slice.push(this.projectIndexCache[i]);
      }
    }
    this.projects = slice;
    this.cachedProjects = slice;
  }

  private async loadProjects(showBusy = false) {
    this.ensureProjectCacheForCurrentQuery();
    const range = this.getVisibleRange();
    this.applyVisibleProjectsFromCache();

    if (this.projectCacheReady && this.cacheCovers(range.start, range.end)) {
      this.isLoadingProjects = false;
      this.loadFavorites();
      this.markVisibleMetricsPending();
      this.loadCardMetricsBatch();
      return;
    }

    const missing = this.getMissingRange(range.start, range.end);
    const skip = missing.start;
    const span = missing.end - missing.start;
    const take = this.pageSize <= 0 && !this.projectCacheReady
      ? 0
      : (span > 0 ? span : (this.pageSize > 0 ? this.pageSize : 0));

    const token = ++this.projectsLoadToken;
    this.projectsLoadInFlight = true;
    this.isLoadingProjects = true;
    const projectsRequest = this.buildProjectsRequest(skip, take);
    // Fired before the projects call is awaited: the metrics command resolves the same page for
    // the same request, so both round trips overlap instead of queueing behind each other.
    const metricsPromise = this.fetchCardMetricsForQuery(projectsRequest);
    try {
      const getCommandResponse: GetProjectsByUserCommandResponse =
        await this.pcs.apiService.GetProjectsByUser(projectsRequest, showBusy, false);
      if (token !== this.projectsLoadToken) {
        return;
      }
      this.mapResponseToModel(getCommandResponse, skip);
      this.markVisibleMetricsPending();
      this.loadCardMetricsBatch(metricsPromise);
    } finally {
      if (token === this.projectsLoadToken) {
        this.projectsLoadInFlight = false;
        this.isLoadingProjects = false;
      }
    }
  }

  mapResponseToModel(getCommandResponse: GetProjectsByUserCommandResponse, skip = 0) {
    const response = this.unwrapProjectsResponse(getCommandResponse);
    const projects = response.Projects || [];

    this.clientList = response.ClientList || [];
    this.cachedAssets = response.AssetList || [];
    this.totalCount = this.resolveProjectTotalCount(response.TotalCount, projects, skip);
    this.projectCacheReady = true;
    this.mergeProjectsIntoCache(projects, skip);
    if (this.pageSize > 0 && this.totalCount > 0) {
      const maxPage = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
    } else if (this.totalCount === 0) {
      this.currentPage = 1;
    }
    this.applyVisibleProjectsFromCache();
    this.userRoles = response.UserRoles;
    this.clientCount = this.clientList.length;
    if (response.FavoriteProjects && response.FavoriteProjects.length) {
      this.favoriteProjects = response.FavoriteProjects;
    }

    if (this.clientList.length === 1 && this.isAllClientsSelected) {
      this.clientControl.setValue(this.clientList[0]);
      this.clientOptionsState.client = this.clientList[0];
      if (this.cachedAssets.length === 1) {
        this.clientOptionsState.asset = this.cachedAssets[0];
      }
    }

    this.updateAssetOptions();
    this.loadFavorites();
    this.markVisibleMetricsPending();
  }

  isCardMetricsPlaceholder(projectId: number): boolean {
    return !!this.metricsPendingById[projectId] && !this.cardMetricsById[projectId];
  }

  private getVisibleMetricProjectIds(): number[] {
    const pageIds = (this.pagedProjects || []).map(p => p.ProjectId).filter(id => !!id);
    const favoriteIds = (this.favoriteProjects || []).map(p => p.ProjectId).filter(id => !!id);
    return Array.from(new Set([...pageIds, ...favoriteIds]));
  }

  private markVisibleMetricsPending() {
    const pending: { [projectId: number]: boolean } = { ...this.metricsPendingById };
    this.getVisibleMetricProjectIds().forEach(id => {
      if (!this.cardMetricsById[id]) {
        pending[id] = true;
      }
    });
    this.metricsPendingById = pending;
    this.isLoadingCardMetrics = Object.keys(pending).some(id => pending[+id] && !this.cardMetricsById[+id]);
  }

  private chunkProjectIds(projectIds: number[], batchSize: number): number[][] {
    const batches: number[][] = [];
    for (let i = 0; i < projectIds.length; i += batchSize) {
      batches.push(projectIds.slice(i, i + batchSize));
    }
    return batches;
  }

  private applyCardMetrics(metrics: ProjectCardMetricsDto[]) {
    if (!metrics || !metrics.length) {
      return;
    }

    const byId: { [projectId: number]: ProjectCardMetricsDto } = { ...this.cardMetricsById };
    metrics.forEach(metric => {
      if (metric && metric.ProjectId) {
        const existing = byId[metric.ProjectId];
        byId[metric.ProjectId] = {
          ...existing,
          ...metric,
          CommPercent: metric.CommPercent != null
            ? metric.CommPercent
            : (existing && existing.CommPercent)
        };
      }
    });
    this.cardMetricsById = byId;
  }

  private async fetchCardMetricsBatch(projectIds: number[]): Promise<ProjectCardMetricsDto[]> {
    const response = await this.pcs.apiService.executeCommandService.executeCommand(
      'GetProjectCardMetrics',
      { ProjectIds: projectIds },
      false,
      false
    );

    return response && response.Payload && response.Payload.Metrics
      ? response.Payload.Metrics
      : [];
  }

  /**
   * Asks for the metrics of whatever projects this request resolves to, so the call does not have
   * to wait for the project list to come back first. Cards already filled in are excluded, matching
   * the id filter the batch pass applies. Never rejects: anything missing from the result is still
   * picked up by the id-based pass in loadCardMetricsBatch.
   */
  private fetchCardMetricsForQuery(request: GetProjectsByUserCommandRequest): Promise<ProjectCardMetricsDto[]> {
    return this.pcs.apiService.executeCommandService.executeCommand(
      'GetProjectCardMetrics',
      {
        ...request,
        UseUserProjectQuery: true,
        ExcludeProjectIds: Object.keys(this.cardMetricsById).map(Number)
      },
      false,
      false
    ).then(response => (response && response.Payload && response.Payload.Metrics)
      ? response.Payload.Metrics
      : [])
      .catch(() => [] as ProjectCardMetricsDto[]);
  }

  private async loadCardMetricsBatch(prefetched?: Promise<ProjectCardMetricsDto[]>) {
    const token = ++this.metricsLoadToken;

    this.markVisibleMetricsPending();

    if (prefetched) {
      this.metricsLoadInFlight = true;
      this.isLoadingCardMetrics = true;
      const prefetchedMetrics = await prefetched;
      if (token !== this.metricsLoadToken) {
        return;
      }
      this.applyCardMetrics(prefetchedMetrics);
    }

    const projectIds = this.getVisibleMetricProjectIds()
      .filter(id => !this.cardMetricsById[id]);

    if (!projectIds.length) {
      this.metricsLoadInFlight = false;
      this.isLoadingCardMetrics = false;
      return;
    }

    this.metricsLoadInFlight = true;
    this.isLoadingCardMetrics = true;

    const batches = this.chunkProjectIds(projectIds, this.cardMetricsBatchSize);

    try {
      await Promise.all(batches.map(async (batch) => {
        try {
          const metrics = await this.fetchCardMetricsBatch(batch);
          if (token !== this.metricsLoadToken) {
            return;
          }
          this.applyCardMetrics(metrics && metrics.length
            ? metrics
            : batch.map(id => ({ ProjectId: id })));
        } catch {
          if (token === this.metricsLoadToken) {
            this.applyCardMetrics(batch.map(id => ({ ProjectId: id })));
          }
        }
      }));
    } finally {
      if (token === this.metricsLoadToken) {
        this.metricsLoadInFlight = false;
        this.isLoadingCardMetrics = false;
      }
    }
  }

  loadState() {
    if (this.clientOptionsState.client != null && this.clientOptionsState.client.ClientId > 0) {
      this.clientControl.setValue(this.clientOptionsState.client);
    }

    if (this.clientOptionsState.asset != null && this.clientOptionsState.asset.AssetId > 0) {
      this.assetControl.setValue(this.clientOptionsState.asset);
    } else {
      this.assetControl.setValue(0);
    }

    if (this.clientOptionsState.filterOption !== "") {
      this.activeProjectsSelected = this.clientOptionsState.filterOption;
    }

    if (this.clientOptionsState.projectSort !== "") {
      this.projectSortControl.setValue(this.clientOptionsState.projectSort);
    }

    this.updateAssetOptions();
  }

  newProject() {
    this.pcs.router.navigate(['new-project']);
  }

  goToFeatureReleases() {
    this.router.navigate(['feature-releases']);
  }

  initializeFeatureViewFromRoute(url: string) {
    // Normalize URL - remove query params and trailing slashes
    const normalizedUrl = url.split('?')[0].replace(/\/$/, '');
    if (normalizedUrl === this.lastInitializedUrl) {
      return;
    }
    this.lastInitializedUrl = normalizedUrl;

    if (normalizedUrl.includes('/feature-releases')) {
      this.viewMode = 'features';
      const segments = normalizedUrl.split('/').filter(Boolean);
      const lastSegment = segments[segments.length - 1];
      const parsed = Number(lastSegment);
      this.isFeatureDetail = !isNaN(parsed) && parsed > 0;
    } else {
      this.viewMode = 'projects';
      this.isFeatureDetail = false;
      this.isLoadingProjects = true;
      this.resetFilters();
      this.loadState();
      if (this.pcs.clientSessionService.loggedIn) {
        this.loadRecentProjects();
        this.loadData();
      }
    }
  }

  resetFilters() {
    this.clientControl.setValue(0);
    this.assetControl.setValue(0);
    this.activeProjectsSelected = 'all';
    this.projectSortControl.setValue('latest-desc');
    this.projectSearchControl.setValue('');
    this.resetPagination();
  }

  clientSelected() {
    const clientId = this.selectedClientId();
    this.clientOptionsState.client = this.clientControl.value;

    if (clientId <= 0) {
      this.assetControl.setValue(0);
      this.clientOptionsState.asset = null;
      this.updateAssetOptions();
      this.reloadPortfolio(true);
      return;
    }

    this.updateAssetOptions();
    this.reloadPortfolio(true);
  }

  filterClients(clientId: number) {
    this.updateAssetOptions(clientId);
  }

  private updateAssetOptions(clientId: number = this.selectedClientId()) {
    if (!this.cachedAssets) {
      this.assetList = [];
      return;
    }

    if (clientId <= 0) {
      this.assetList = [];
      return;
    }

    this.assetList = this.cachedAssets.filter(item => item.Client && item.Client.ClientId === clientId);
    if (this.assetList.length === 1) {
      this.assetControl.setValue(this.assetList[0]);
      this.clientOptionsState.asset = this.assetList[0];
      return;
    }

    const selectedAssetId = this.selectedAssetId();
    if (selectedAssetId > 0 && !this.assetList.find(a => a.AssetId === selectedAssetId)) {
      this.assetControl.setValue(0);
      this.clientOptionsState.asset = null;
    }
  }

  assetSelected() {
    this.clientOptionsState.asset = this.assetControl.value;
    this.reloadPortfolio(true);
  }

  filterAssets(assetId: number, clientId: number, isActive: boolean) {
    this.reloadPortfolio(true);
  }

  projectSortChanged() {
    this.clientOptionsState.projectSort = this.projectSortControl.value;
    this.reloadPortfolio(true);
  }

  sortProjects(value: string) {
    this.projectSortControl.setValue(value);
    this.reloadPortfolio(true);
  }

  setActive(value: string): boolean {
    switch (value) {
      case "active":
        return true;
      case "inactive":
        return false;
      default:
        return null;
    }
  }

  onProjectTypeChange(value: string) {
    this.clientOptionsState.filterOption = value;
    this.activeProjectsSelected = value;
    this.reloadPortfolio(true);
  }

  onProjectSearchChange() {
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => {
      this.reloadPortfolio(true);
    }, 300);
  }

  get selectedClientLabel(): string {
    const value = this.clientControl.value;
    return (value && typeof value === 'object' && value.Name) ? value.Name : 'All Clients';
  }

  get selectedAssetLabel(): string {
    const value = this.assetControl.value;
    return (value && typeof value === 'object' && value.Name) ? value.Name : 'All Assets';
  }

  get isAllClientsSelected(): boolean {
    const value = this.clientControl.value;
    return !(value && typeof value === 'object' && value.ClientId);
  }

  get isAllAssetsSelected(): boolean {
    const value = this.assetControl.value;
    return !(value && typeof value === 'object' && value.AssetId);
  }

  @HostListener('document:click')
  closeFilterMenus() {
    this.clientMenuOpen = false;
    this.assetMenuOpen = false;
  }

  toggleClientMenu(event: MouseEvent) {
    event.stopPropagation();
    this.clientMenuOpen = !this.clientMenuOpen;
    this.assetMenuOpen = false;
  }

  toggleAssetMenu(event: MouseEvent) {
    event.stopPropagation();
    this.assetMenuOpen = !this.assetMenuOpen;
    this.clientMenuOpen = false;
  }

  chooseClient(client: ClientDto | 0) {
    this.clientControl.setValue(client);
    this.clientMenuOpen = false;
    this.clientSelected();
  }

  chooseAsset(asset: AssetDto | 0) {
    this.assetControl.setValue(asset);
    this.assetMenuOpen = false;
    this.assetSelected();
  }

  isClientOptionSelected(client: ClientDto): boolean {
    const value = this.clientControl.value;
    return !!(value && value.ClientId === client.ClientId);
  }

  isAssetOptionSelected(asset: AssetDto): boolean {
    const value = this.assetControl.value;
    return !!(value && value.AssetId === asset.AssetId);
  }

  compareClients(c1, c2) {
    return c1 && c2 ? c1.ClientId === c2.ClientId : c1 === c2;
  }

  compareAssets(a1, a2) {
    return a1 && a2 ? a1.AssetId === a2.AssetId : a1 === a2;
  }
}
